import { useTranslation } from 'react-i18next';
import { ArrowRightLeft } from 'lucide-react';

/**
 * Compact handover indicator for the app shell header, next to the date and
 * clocked-in status (desktop) resp. the page title (mobile). Renders:
 *   - desktop (lg+): amber pill with icon + label + optional count badge
 *   - mobile/tablet: icon-only button with an alert dot
 * Shown only when an unacknowledged handover waits for the current user
 * (status = 'submitted', addressed to them); clicking opens the handover
 * detail modal via onOpen. Realtime-updated via the centralized
 * realtimeStore → the underlying TanStack query refetches automatically.
 *
 * @param {Object} props
 * @param {Object[]} props.incoming - Unacknowledged handovers addressed to me.
 * @param {Function} props.onOpen - Called with the handover row when clicked.
 */
export function HandoverBanner({ incoming, onOpen }) {
    const { t } = useTranslation();

    if (!incoming || incoming.length === 0) return null;

    const first = incoming[0];

    return (
        <>
            {/* Desktop: text pill next to date / clock status */}
            <button
                onClick={() => onOpen(first)}
                title={t('handover.banner.subtitle', {
                    date: first.handover_date,
                    shift: t(`handover.shift.${first.shift_label}`),
                })}
                className="hidden lg:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 hover:bg-amber-100 transition-all text-amber-800 shrink-0 cursor-pointer"
            >
                <ArrowRightLeft size={14} className="text-amber-600 shrink-0" />
                <span className="text-xs font-bold whitespace-nowrap">
                    {t('handover.banner.title')}
                </span>
                {incoming.length > 1 && (
                    <span className="w-5 h-5 flex items-center justify-center rounded-full bg-amber-500 text-white text-[10px] font-black shrink-0">
                        {incoming.length}
                    </span>
                )}
            </button>

            {/* Mobile / tablet: icon-only button with alert dot */}
            <button
                onClick={() => onOpen(first)}
                aria-label={t('handover.banner.title')}
                className="lg:hidden relative p-1 bg-transparent hover:text-amber-600 transition-colors shrink-0 text-amber-600 active:scale-[0.95]"
            >
                <ArrowRightLeft size={20} />
                <span className="absolute top-0 right-0 w-2 h-2 bg-amber-500 rounded-full ring-2 ring-white" />
            </button>
        </>
    );
}